import { DeleteDialogContent } from '@components/dialogs/delete.dialog';
import { DuplicateDialogContent } from '@components/dialogs/duplicate.dialog';
import { ProcessTagBadge } from '@components/process-tag-badge';
import { Avatar, AvatarFallback, AvatarImage } from '@components/ui/avatar';
import { Dialog, DialogTrigger } from '@components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@components/ui/dropdown-menu';
import useStores from '@hooks/useStore';
import type { ProcessModel } from '@models/process.model';
import { hasElevatedRights } from '@stratumn/studio-shared';
import {
  Activity,
  Copy,
  MoreHorizontal,
  Pencil,
  Pyramid,
  Trash
} from 'lucide-react';
import { observer } from 'mobx-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useUser } from '@/layout';
import { cn } from '@/lib/utils';
import { TRACE_UI_URL } from '@/utils/constants';

import { ViewType } from '../home.utils';
import { getTimeAgo } from './processCard.utils';

const getProcessInitials = (name: string): string => {
  const words = name.trim().split(/\s+/);
  if (words.length >= 2) {
    return `${words[0][0]}${words[1][0]}`.toUpperCase();
  }
  return (words[0]?.[0] ?? '').toUpperCase();
};

type ProcessCardProps = {
  process: ProcessModel;
  view: ViewType;
  loading?: boolean;
};

const ProcessCard = ({ process, view, loading = false }: ProcessCardProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDuplicateDialogOpen, setIsDuplicateDialogOpen] = useState(false);
  const navigate = useNavigate();
  const hasBeenGenerated =
    (process &&
      process.workflows.length > 0 &&
      process.workflows[0]?.published_id) ||
    process?.published;

  const { processStore } = useStores();
  const { user } = useUser();
  const isPrivileged = hasElevatedRights(user);

  async function deleteProcess() {
    const response = await processStore.deleteProcess(process.id);
    if (response) {
      window.location.assign('/');
    }
  }

  async function handleDuplicate(
    options: Parameters<typeof processStore.duplicateProcess>[1]
  ) {
    const result = await processStore.duplicateProcess(process.id, options);
    if (result) {
      window.location.assign('/');
    }
  }

  const stratumnEditorPath = `${process.id}`;

  const isGrid = view === ViewType.Grid;

  return (
    <Link
      className={cn(
        'relative flex flex-col rounded-lg bg-white shadow-verail transition-shadow duration-150 hover:shadow-verailHover',
        isGrid
          ? 'min-h-[168px] px-4 pt-5 pb-2.5 pl-6'
          : 'min-h-0 px-4 py-3 pl-6',
        loading && 'cursor-default'
      )}
      draggable="false"
      to={loading ? '' : `${stratumnEditorPath}/general`}
    >
      <header className="flex w-full shrink-0 items-center gap-4">
        {/* Process icon */}
        <Avatar className="size-8">
          <AvatarImage alt="" className="object-cover" src={process.icon} />
          <AvatarFallback className="bg-primary/10 font-medium text-primary text-xs">
            {getProcessInitials(process.name)}
          </AvatarFallback>
        </Avatar>

        {/* Name + subtitle */}
        <div className="flex min-w-0 flex-1 flex-col justify-center">
          <div className="flex items-center gap-2 overflow-hidden">
            <span className="truncate font-medium text-foreground text-sm">
              {process.name}
            </span>
            <ProcessTagBadge tag={process.tag} />
          </div>
          {!process.draft && (
            <span className="truncate text-muted-foreground text-xs">
              Process with 1 workflow
            </span>
          )}
        </div>

        {/* Footer inline for list view */}
        {!isGrid && (
          <div className="flex shrink-0 items-center gap-2 pr-4">
            <Activity className="text-muted-foreground" size={14} />
            <span className="whitespace-nowrap text-muted-foreground text-xs">
              Last updated {getTimeAgo(process.createdAt)}
            </span>
          </div>
        )}

        {/* Right side: status dot + menu */}
        <div className="flex shrink-0 items-center gap-3">
          {!(loading || process.draft) && (
            <span
              className={cn(
                'size-2 rounded-full',
                process.published
                  ? 'animate-pulse bg-emerald-400'
                  : 'animate-pulse bg-amber-400'
              )}
              title={
                process.published
                  ? 'This workflow is in production'
                  : 'This workflow is not yet in production'
              }
            />
          )}

          <Dialog onOpenChange={setIsDialogOpen} open={isDialogOpen}>
            <DropdownMenu>
              <DropdownMenuTrigger
                className="rounded-md p-2 hover:bg-muted focus:outline-none"
                onClick={e => e.stopPropagation()}
              >
                <MoreHorizontal size={16} />
              </DropdownMenuTrigger>
              <DropdownMenuContent onClick={e => e.stopPropagation()}>
                <DropdownMenuItem
                  onClick={e => {
                    e.stopPropagation();
                    navigate(`${stratumnEditorPath}/general`);
                  }}
                >
                  <Pencil size={16} /> Editor
                </DropdownMenuItem>
                {!process.draft && hasBeenGenerated && (
                  <DropdownMenuItem
                    onClick={e => {
                      e.stopPropagation();
                      window.open(
                        `${TRACE_UI_URL}/workflowoverview/${process?.workflows[0]?.published_id}`,
                        '_blank',
                        'noopener,noreferrer'
                      );
                    }}
                  >
                    <Pyramid size={16} /> Open in Trace
                  </DropdownMenuItem>
                )}
                {isPrivileged && process.draft && (
                  <DropdownMenuItem
                    onClick={e => {
                      e.stopPropagation();
                      setIsDuplicateDialogOpen(true);
                    }}
                  >
                    <Copy size={16} /> Duplicate
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DialogTrigger asChild>
                  <DropdownMenuItem
                    className="focus:text-red-600"
                    onClick={e => e.stopPropagation()}
                  >
                    <Trash size={16} /> Delete
                  </DropdownMenuItem>
                </DialogTrigger>
              </DropdownMenuContent>
            </DropdownMenu>
            <DeleteDialogContent
              isDraft={process.draft}
              nuke={deleteProcess}
              setOpen={setIsDialogOpen}
            />
          </Dialog>
          <Dialog
            onOpenChange={setIsDuplicateDialogOpen}
            open={isDuplicateDialogOpen}
          >
            <DuplicateDialogContent
              onDuplicate={handleDuplicate}
              processName={process.name}
              setOpen={setIsDuplicateDialogOpen}
            />
          </Dialog>
        </div>
      </header>

      {/* Grid view: spacer + footer */}
      {isGrid && (
        <>
          <div className="mt-4 flex-1" />
          <footer className="flex h-6 shrink-0 items-center gap-2">
            <Activity className="text-muted-foreground" size={14} />
            <span className="whitespace-nowrap text-muted-foreground text-xs">
              Last updated {getTimeAgo(process.createdAt)}
            </span>
          </footer>
        </>
      )}
    </Link>
  );
};

export default observer(ProcessCard);
